import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { api } from '../api/client';
import { useAsync } from '../hooks/useAsync';
import { useSideNav } from '../context/SideNavContext';
import { useAuth } from '../context/AuthContext';
import Card from '../components/Card';
import StatCard from '../components/StatCard';
import StatusBadge from '../components/StatusBadge';
import UserGroupForm from '../components/UserGroupForm';
import PolicyForm from '../components/PolicyForm';
import InlineForm from '../components/InlineForm';
import ConfirmDialog from '../components/ConfirmDialog';
import { Loading } from '../components/States';
import Icon from '../components/Icon';
import { number, relativeTime, titleCase } from '../lib/format';
import { ICONS } from '../lib/icons';
import { toId } from '../lib/ids';

const USER_ROLES = [
  { id: 'member', label: 'Member' },
  { id: 'org-admin', label: 'Org admin' },
  { id: 'billing', label: 'Billing' },
  { id: 'superadmin', label: 'Superadmin' },
];
const USER_STATUSES = ['active', 'invited', 'suspended'];

// The IAM service left menu. Only the areas this console actually implements
// are listed: users and user groups. Roles, policies, identity providers,
// account settings, root access, temporary delegation, the Access reports
// group, and the related-consoles links are all hidden — they were
// placeholders with no backing data. Contract management lives in the main
// console menu (Sidebar.jsx, Administration) rather than being duplicated here.
const NAV = {
  title: 'Identity and Access Management (IAM)',
  crumb: 'IAM',
  backTo: '/services',
  search: true,
  items: [{ label: 'Dashboard', to: '/iam/dashboard' }],
  groups: [
    {
      title: 'Access Management',
      items: [
        { label: 'IAM users', to: '/iam/users' },
        { label: 'IAM user groups', to: '/iam/user-groups' },
        { label: 'IAM policies', to: '/iam/policies' },
      ],
    },
  ],
};

const SECTIONS = {
  dashboard: {
    title: 'IAM Dashboard',
    desc: 'Overview of identities and access in this account.',
    kind: 'dashboard',
  },
  users: {
    title: 'Users',
    desc: 'An IAM user is an identity with long-term credentials used to interact with the cloud.',
    columns: ['User name', 'Email', 'Role', 'Status', 'Last active'],
    create: 'Create user',
    live: true,
  },
  'user-groups': {
    title: 'IAM user groups',
    desc: 'A user group is a collection of IAM users. Use groups to specify permissions for a collection of users.',
    columns: ['Group name', 'Users', 'OCI tenant', 'Creation time'],
    create: 'Create group',
  },
  policies: {
    title: 'Policies',
    desc: 'A policy grants a user group access to a set of services. Assign a group the services its members may use.',
    columns: ['Policy name', 'User group', 'Services', 'Creation time'],
    create: 'Create policy',
  },
};

export default function Iam() {
  const { section = 'user-groups' } = useParams();
  const { setNav } = useSideNav();
  const { isAdmin } = useAuth();
  const { data, loading, reload } = useAsync(
    () =>
      Promise.all([
        api.getUsers(),
        api.getUserGroups(),
        api.getPolicies().catch(() => []),
        // Organizations are superadmin-only; non-admins simply get no list.
        isAdmin ? api.getOrganizations().catch(() => []) : Promise.resolve([]),
      ]).then(([users, groups, policies, orgs]) => ({
        users,
        groups,
        policies,
        orgs,
      })),
    [isAdmin],
  );
  const users = data?.users || [];
  const groups = data?.groups || [];
  const policies = data?.policies || [];
  const orgs = data?.orgs || [];
  const [showCreateGroup, setShowCreateGroup] = useState(false);
  const [editGroup, setEditGroup] = useState(null);

  // Policy create/edit is inline; `showCreatePolicy` opens a blank form and
  // `editPolicy` holds the policy being edited. `confirmPolicy` drives the
  // delete confirmation modal.
  const [showCreatePolicy, setShowCreatePolicy] = useState(false);
  const [editPolicy, setEditPolicy] = useState(null);
  const [confirmPolicy, setConfirmPolicy] = useState(null);

  // User create/edit is an inline form: `userForm` holds the draft and
  // `userEditing` is the id being edited (null while creating).
  const [userForm, setUserForm] = useState(null);
  const [userEditing, setUserEditing] = useState(null);
  const [userSaving, setUserSaving] = useState(false);
  const [userError, setUserError] = useState(null);

  // Setting a password is its own inline form, separate from the profile edit:
  // saving a name should never require re-entering a password, and vice versa.
  const [pwdUser, setPwdUser] = useState(null);
  const [pwd, setPwd] = useState({ password: '', confirm: '' });
  const [pwdSaving, setPwdSaving] = useState(false);
  const [pwdError, setPwdError] = useState(null);
  const [pwdDone, setPwdDone] = useState(null);

  // Pending delete — the only thing here that opens a modal.
  const [confirmUser, setConfirmUser] = useState(null);
  const [confirmBusy, setConfirmBusy] = useState(false);
  const [confirmError, setConfirmError] = useState(null);

  const openEditGroup = (id) => {
    api
      .getUserGroup(id)
      .then(setEditGroup)
      .catch(() => {});
  };

  const startCreateUser = () => {
    // Only one inline form at a time.
    setPwdUser(null);
    setUserEditing(null);
    setUserError(null);
    setUserForm({
      name: '',
      email: '',
      orgId: String(orgs[0]?.id ?? ''),
      role: 'member',
      status: 'invited',
    });
  };

  const startEditUser = (u) => {
    // Only one inline form at a time.
    setPwdUser(null);
    setUserEditing(u.id);
    setUserError(null);
    setUserForm({
      name: u.name,
      email: u.email,
      orgId: String(u.orgId ?? ''),
      role: u.role,
      status: u.status,
    });
  };

  const closeUserForm = () => {
    setUserForm(null);
    setUserEditing(null);
    setUserError(null);
  };

  const updateUserField = (key) => (e) =>
    setUserForm((f) => ({ ...f, [key]: e.target.value }));

  const submitUser = async (e) => {
    e.preventDefault();
    if (!userForm.name.trim() || !userForm.email.trim()) {
      setUserError('Name and email are required');
      return;
    }
    setUserSaving(true);
    setUserError(null);
    try {
      if (userEditing) {
        await api.updateUser(userEditing, {
          name: userForm.name.trim(),
          email: userForm.email.trim(),
          orgId: toId(userForm.orgId),
          role: userForm.role,
          status: userForm.status,
        });
      } else {
        await api.createUser({
          name: userForm.name.trim(),
          email: userForm.email.trim(),
          orgId: toId(userForm.orgId),
          role: userForm.role,
        });
      }
      closeUserForm();
      reload();
    } catch (err) {
      setUserError(err.message);
    } finally {
      setUserSaving(false);
    }
  };

  const MIN_PASSWORD = 8;

  const startSetPassword = (u) => {
    closeUserForm();
    setPwdUser(u);
    setPwd({ password: '', confirm: '' });
    setPwdError(null);
    setPwdDone(null);
  };

  const closePwdForm = () => {
    setPwdUser(null);
    setPwdError(null);
  };

  const submitPassword = async (e) => {
    e.preventDefault();
    if (pwd.password.length < MIN_PASSWORD) {
      setPwdError(`Password must be at least ${MIN_PASSWORD} characters`);
      return;
    }
    if (pwd.password !== pwd.confirm) {
      setPwdError('The two passwords do not match');
      return;
    }
    setPwdSaving(true);
    setPwdError(null);
    try {
      await api.setUserPassword(pwdUser.id, pwd.password);
      setPwdDone(pwdUser.name);
      setPwdUser(null);
      setPwd({ password: '', confirm: '' });
    } catch (err) {
      setPwdError(err.message);
    } finally {
      setPwdSaving(false);
    }
  };

  const deleteUser = async () => {
    setConfirmBusy(true);
    setConfirmError(null);
    try {
      await api.deleteUser(confirmUser.id);
      setConfirmUser(null);
      reload();
    } catch (err) {
      setConfirmError(err.message);
    } finally {
      setConfirmBusy(false);
    }
  };

  const deletePolicy = async () => {
    setConfirmBusy(true);
    setConfirmError(null);
    try {
      await api.deletePolicy(confirmPolicy.id);
      setConfirmPolicy(null);
      reload();
    } catch (err) {
      setConfirmError(err.message);
    } finally {
      setConfirmBusy(false);
    }
  };

  useEffect(() => {
    setNav(NAV);
    return () => setNav(null);
  }, [setNav]);

  const cfg = SECTIONS[section] || SECTIONS['user-groups'];

  if (cfg.kind === 'dashboard') {
    const roles = new Set((users || []).map((u) => u.role));
    return (
      <div className="stack">
        <div className="listview__head">
          <div>
            <h1 className="page-header__title">{cfg.title}</h1>
            <p className="listview__desc">{cfg.desc}</p>
          </div>
        </div>
        <div className="grid grid--kpi">
          <StatCard
            label="Users"
            value={number((users || []).length)}
            hint="IAM users"
          />
          <StatCard
            label="User groups"
            value={number((groups || []).length)}
            hint="collections"
          />
          <StatCard
            label="Roles"
            value={number(roles.size)}
            hint="distinct roles"
          />
        </div>
      </div>
    );
  }

  const isUsers = cfg.live;
  const isGroups = section === 'user-groups';
  const isPolicies = section === 'policies';
  const rows = isUsers ? users : isGroups ? groups : isPolicies ? policies : [];
  const hasActions = isGroups || isUsers || isPolicies;

  const handleSavedGroup = () => {
    setShowCreateGroup(false);
    setEditGroup(null);
    reload();
  };

  const handleSavedPolicy = () => {
    setShowCreatePolicy(false);
    setEditPolicy(null);
    reload();
  };

  return (
    <div className="stack">
      <div className="listview__head">
        <div>
          <h1 className="page-header__title inline-title">
            {cfg.title} <span className="count-badge">({rows.length})</span>
            <a
              className="link"
              href="#info"
              onClick={(e) => e.preventDefault()}
            >
              Info
            </a>
          </h1>
          <p className="listview__desc">{cfg.desc}</p>
        </div>
        <div className="page-header__actions">
          <button
            className="icon-btn icon-btn--round"
            title="Refresh"
            aria-label="Refresh"
            onClick={reload}
          >
            <Icon name={ICONS.refresh} />
          </button>
          {cfg.create ? (
            <button className="btn btn--ghost" disabled>
              Delete
            </button>
          ) : null}
          {cfg.create ? (
            <button
              className="btn btn--primary"
              onClick={
                isGroups
                  ? () => setShowCreateGroup(true)
                  : isUsers
                    ? startCreateUser
                    : isPolicies
                      ? () => {
                          setEditPolicy(null);
                          setShowCreatePolicy(true);
                        }
                      : undefined
              }
              disabled={(isGroups || isUsers || isPolicies) && !isAdmin}
              title={
                (isGroups || isUsers || isPolicies) && !isAdmin
                  ? 'Superadmin role required'
                  : undefined
              }
            >
              {cfg.create}
            </button>
          ) : null}
        </div>
      </div>

      <Card>
        {isGroups && (showCreateGroup || editGroup) ? (
          <UserGroupForm
            users={users}
            group={editGroup || undefined}
            onClose={() => {
              setShowCreateGroup(false);
              setEditGroup(null);
            }}
            onSaved={handleSavedGroup}
          />
        ) : null}

        {isPolicies && (showCreatePolicy || editPolicy) ? (
          <PolicyForm
            groups={groups}
            policy={editPolicy || undefined}
            onClose={() => {
              setShowCreatePolicy(false);
              setEditPolicy(null);
            }}
            onSaved={handleSavedPolicy}
          />
        ) : null}

        {isUsers && pwdUser ? (
          <InlineForm
            title={`Set password — ${pwdUser.name}`}
            onSubmit={submitPassword}
            onCancel={closePwdForm}
            submitting={pwdSaving}
            submitLabel="Set password"
            error={pwdError}
          >
            <div className="form-grid">
              <label className="field">
                <span>New password</span>
                <input
                  className="input"
                  type="password"
                  autoComplete="new-password"
                  value={pwd.password}
                  onChange={(e) => setPwd((p) => ({ ...p, password: e.target.value }))}
                  autoFocus
                  required
                />
                <span className="field__hint">
                  At least {MIN_PASSWORD} characters.
                </span>
              </label>
              <label className="field">
                <span>Confirm password</span>
                <input
                  className="input"
                  type="password"
                  autoComplete="new-password"
                  value={pwd.confirm}
                  onChange={(e) => setPwd((p) => ({ ...p, confirm: e.target.value }))}
                  required
                />
              </label>
            </div>
            <span className="field__hint">
              {pwdUser.email} can sign in with this immediately. Any session they
              already have stays valid until it expires.
            </span>
          </InlineForm>
        ) : null}

        {isUsers && pwdDone ? (
          <p className="field__hint">
            Password updated for <strong>{pwdDone}</strong>.
          </p>
        ) : null}

        {isUsers && userForm ? (
          <InlineForm
            title={userEditing ? 'Edit user' : 'Create user'}
            onSubmit={submitUser}
            onCancel={closeUserForm}
            submitting={userSaving}
            submitLabel={userEditing ? 'Save changes' : 'Create user'}
            error={userError}
          >
            <div className="form-grid">
              <label className="field">
                <span>User name</span>
                <input
                  className="input"
                  placeholder="e.g. Jordan Lee"
                  value={userForm.name}
                  onChange={updateUserField('name')}
                  autoFocus
                  required
                />
              </label>
              <label className="field">
                <span>Email</span>
                <input
                  className="input"
                  type="email"
                  placeholder="e.g. jordan@company.com"
                  value={userForm.email}
                  onChange={updateUserField('email')}
                  required
                />
              </label>
              <label className="field">
                <span>Organization</span>
                <select
                  className="input"
                  value={userForm.orgId}
                  onChange={updateUserField('orgId')}
                >
                  {orgs.map((o) => (
                    <option key={o.id} value={o.id}>
                      {o.name}
                    </option>
                  ))}
                </select>
              </label>
              <label className="field">
                <span>Role</span>
                <select
                  className="input"
                  value={userForm.role}
                  onChange={updateUserField('role')}
                >
                  {USER_ROLES.map((r) => (
                    <option key={r.id} value={r.id}>
                      {r.label}
                    </option>
                  ))}
                </select>
              </label>
              {userEditing ? (
                <label className="field">
                  <span>Status</span>
                  <select
                    className="input"
                    value={userForm.status}
                    onChange={updateUserField('status')}
                  >
                    {USER_STATUSES.map((s) => (
                      <option key={s} value={s}>
                        {titleCase(s)}
                      </option>
                    ))}
                  </select>
                </label>
              ) : null}
            </div>
            {!userEditing ? (
              <span className="field__hint">
                The user is created with an <strong>invited</strong> status.
              </span>
            ) : null}
          </InlineForm>
        ) : null}

        <div className="listview__toolbar">
          <input
            className="input search"
            placeholder="Search"
            aria-label="Search"
          />
          <span className="listview__page">
            <Icon name={ICONS.chevronLeft} /> 1 <Icon name={ICONS.chevronRight} />
          </span>
        </div>
        <div className="table-wrap">
          <table className="table">
            <thead>
              <tr>
                {cfg.columns.map((c) => (
                  <th key={c}>{c}</th>
                ))}
                {hasActions ? <th aria-label="actions" /> : null}
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan={cfg.columns.length + (hasActions ? 1 : 0)}>
                    <Loading />
                  </td>
                </tr>
              ) : rows.length === 0 ? (
                <tr>
                  <td
                    colSpan={(cfg.columns.length || 1) + (hasActions ? 1 : 0)}
                    className="table__empty"
                  >
                    No resources to display
                  </td>
                </tr>
              ) : isGroups ? (
                rows.map((g) => (
                  <tr key={g.id}>
                    <td className="cell-primary">{g.name}</td>
                    <td>{number(g.userCount || 0)}</td>
                    <td className="cell-type">{g.tenancyName || '—'}</td>
                    <td>{new Date(g.createdAt).toLocaleString()}</td>
                    <td className="num">
                      <button
                        className="btn btn--ghost btn--sm"
                        onClick={() => openEditGroup(g.id)}
                      >
                        Edit
                      </button>
                    </td>
                  </tr>
                ))
              ) : isPolicies ? (
                rows.map((p) => (
                  <tr key={p.id}>
                    <td className="cell-primary">{p.name}</td>
                    <td className="cell-type">{p.groupName || '—'}</td>
                    <td>
                      {p.services && p.services.length > 0
                        ? p.services.map((s) => s.name || s.id).join(', ')
                        : '—'}
                    </td>
                    <td>{new Date(p.createdAt).toLocaleString()}</td>
                    <td className="num">
                      {isAdmin ? (
                        <div className="row-actions">
                          <button
                            className="btn btn--ghost btn--sm"
                            onClick={() => {
                              setShowCreatePolicy(false);
                              setEditPolicy(p);
                            }}
                          >
                            Edit
                          </button>
                          <button
                            className="btn btn--danger-ghost btn--sm"
                            onClick={() => setConfirmPolicy(p)}
                          >
                            Delete
                          </button>
                        </div>
                      ) : null}
                    </td>
                  </tr>
                ))
              ) : (
                rows.map((u) => (
                  <tr key={u.id}>
                    <td className="cell-primary">{u.name}</td>
                    <td>{u.email}</td>
                    <td>{titleCase(u.role)}</td>
                    <td>
                      <StatusBadge status={u.status} />
                    </td>
                    <td>{relativeTime(u.lastActive)}</td>
                    <td className="num">
                      {isAdmin ? (
                        <div className="row-actions">
                          <button
                            className="btn btn--ghost btn--sm"
                            onClick={() => startEditUser(u)}
                          >
                            Edit
                          </button>
                          <button
                            className="btn btn--ghost btn--sm"
                            onClick={() => startSetPassword(u)}
                          >
                            Password
                          </button>
                          <button
                            className="btn btn--danger-ghost btn--sm"
                            onClick={() => setConfirmUser(u)}
                          >
                            Delete
                          </button>
                        </div>
                      ) : null}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </Card>

      {confirmUser ? (
        <ConfirmDialog
          title="Delete user"
          message={`Delete ${confirmUser.name} (${confirmUser.email})?`}
          detail="They lose access immediately and drop out of every user group."
          confirmLabel="Delete user"
          busy={confirmBusy}
          error={confirmError}
          onConfirm={deleteUser}
          onCancel={() => {
            setConfirmUser(null);
            setConfirmError(null);
          }}
        />
      ) : null}

      {confirmPolicy ? (
        <ConfirmDialog
          title="Delete policy"
          message={`Delete policy ${confirmPolicy.name}?`}
          detail="The user group loses the service access this policy granted."
          confirmLabel="Delete policy"
          busy={confirmBusy}
          error={confirmError}
          onConfirm={deletePolicy}
          onCancel={() => {
            setConfirmPolicy(null);
            setConfirmError(null);
          }}
        />
      ) : null}
    </div>
  );
}
